import { CREATE_TEMPLATE_QUERY } from 'modules/manager/domain/create-template-query';
import { MediaKind, isKnownMediaKind } from 'modules/manager/domain/media-kind';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Args {
  /** The group is loaded: before that it is unknown whether the user may write to it. */
  isReady: boolean;
  canWrite: boolean;
  onCreate: (mediaKind: MediaKind) => void;
}

/**
 * `?create=<MediaKind>` on a group page (IMGM-1518): once the group is known, opens the create
 * dialog with that kind and strips the parameter with a `replace` navigation, keeping the other
 * parameters and the hash — so F5 or «Back» does not open the dialog again. An unknown kind, or a
 * group the user may not write to, only strips the parameter.
 *
 * Under StrictMode the effect runs twice in dev: opening is idempotent, and so is the strip.
 */
export function useCreateTemplateQueryParam({ isReady, canWrite, onCreate }: Args): void {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get(CREATE_TEMPLATE_QUERY);
    if (raw == null || !isReady) return;

    if (canWrite && isKnownMediaKind(raw)) {
      onCreate(raw);
    }

    params.delete(CREATE_TEMPLATE_QUERY);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location.pathname, location.search, location.hash, location.state, isReady, canWrite, onCreate, navigate]);
}
